import type { AppointmentSubmission } from '../models/appointment-submission';
import type { PublicConsultationType } from '../models/public-consultation-type';
import type { PublicDayAvailability } from '../models/public-day-availability';
import type { SubmitAppointmentRequest } from '../models/submit-appointment-request';
import { apiGet, apiPost } from './api-client';

/**
 * The anonymous booking routes. All three are rate-limited on the caller's address, and the reads
 * and the write are **two separate budgets** — a customer who books nothing legitimately makes a
 * dozen reads while one who books writes once, so one shared budget would let browsing exhaust
 * booking.
 */
export async function getConsultationTypes(signal?: AbortSignal): Promise<PublicConsultationType[]> {
    return apiGet<PublicConsultationType[]>('/public/consultation-types', {
        signal
    });
}

/**
 * One range call per view, never one call per day.
 *
 * The live form fetches a single date at a time, prefetches eight of them on entry and caches
 * `av[ds]` for the life of the page with no invalidation — so a slot taken while the customer
 * types their name still shows as free, and the 409 is the first they hear of it. The endpoint
 * takes `from` and `days` precisely so a window is one request, which is also what keeps a slow
 * scroll through the calendar inside the read budget.
 *
 * `from` is **deliberately not sent**. It would have to be today's date, and today is a different
 * date in the device's zone than in `Appointments.TimeZone` — the same reason the browser must not
 * compute the instant. Omitted, the server starts the window at its own today, and one call covers
 * the whole bookable horizon.
 *
 * `memberId` asks for one consultant's diary instead of the team's, when the customer picked one
 * before reaching the calendar. Sending it is what makes the times on offer times *that person* is
 * free at — the pooled calendar answers where anybody has room, which is a different question once
 * the request will carry a name.
 */
export async function getFreeSlots(
    consultationTypeId: string,
    days: number,
    memberId: string | null,
    signal?: AbortSignal
): Promise<PublicDayAvailability[]> {
    const query = new URLSearchParams({ consultationTypeId, days: String(days) });

    if (memberId !== null) {
        query.set('memberId', memberId);
    }

    return apiGet<PublicDayAvailability[]>(`/public/appointments/slots?${query.toString()}`, { signal });
}

/**
 * Sends the request. Answers **202** with no identifier: the request has been accepted and is not
 * yet a booking — staff confirm it.
 *
 * A retried submission is safe by the server's construction, not by the button's: one matching a
 * live `New` appointment on phone, service and picked time inside the hold window is treated as
 * that same request and returns its answer.
 */
export async function submitAppointment(request: SubmitAppointmentRequest): Promise<AppointmentSubmission> {
    return apiPost<AppointmentSubmission>('/public/appointments', {
        body: request
    });
}
